import React, { useState, useEffect, useCallback } from 'react';
import { FiSave, FiX, FiUpload, FiTrash2, FiCopy } from 'react-icons/fi';
import { useAuthStore } from '../store/useAuthStore';
import '../styles/PageEditor.css';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Pages are now loaded from the admin dashboard

export default function PageEditor({ onClose, selectedPageId = null, availablePages = [] }) {
  const token = useAuthStore((state) => state.token);
  const [selectedPage, setSelectedPage] = useState(selectedPageId || (availablePages[0]?.id) || '');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [pageFiles, setPageFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [fileUploadMessage, setFileUploadMessage] = useState('');

  const fetchPageContent = useCallback(async (pageId) => {
    setLoading(true);
    setMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/api/pages/${pageId}?admin=true`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setTitle(data.title || '');
        setContent(data.content || '');
      } else if (response.status === 404) {
        // Page doesn't exist yet, set to empty
        const page = availablePages.find((p) => p.id === pageId);
        setTitle(page?.title || '');
        setContent('');
      }
    } catch (error) {
      console.error('Error fetching page:', error);
      setMessage('Error loading page content');
    } finally {
      setLoading(false);
    }
  }, [token, availablePages]);

  const fetchPageFiles = useCallback(async (pageId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/pages/${pageId}/files`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setPageFiles(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error fetching page files:', error);
      setPageFiles([]);
    }
  }, [token]);

  useEffect(() => {
    if (selectedPage) {
      fetchPageContent(selectedPage);
      fetchPageFiles(selectedPage);
    }
  }, [selectedPage, fetchPageContent, fetchPageFiles]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/pages/${selectedPage}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ title, content }),
      });

      if (response.ok) {
        setMessage('Page saved successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage('Error saving page');
      }
    } catch (error) {
      console.error('Error saving page:', error);
      setMessage('Error saving page');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setFileUploadMessage('');

    for (let file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_BASE_URL}/api/pages/${selectedPage}/upload`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });

        if (response.ok) {
          setFileUploadMessage(`✓ ${file.name} uploaded successfully`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          fetchPageFiles(selectedPage);
        } else {
          setFileUploadMessage(`✗ Error uploading ${file.name}`);
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        setFileUploadMessage(`✗ Error uploading ${file.name}`);
      }
    }

    setUploading(false);
    // Clear file input
    e.target.value = '';
  };

  const handleDeleteFile = async (fileId) => {
    if (window.confirm('Delete this file?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/pages/${selectedPage}/files/${fileId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          setFileUploadMessage('File deleted successfully');
          fetchPageFiles(selectedPage);
          setTimeout(() => setFileUploadMessage(''), 2000);
        } else {
          setFileUploadMessage('Error deleting file');
        }
      } catch (error) {
        console.error('Error deleting file:', error);
        setFileUploadMessage('Error deleting file');
      }
    }
  };

  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    setFileUploadMessage('Link copied to clipboard!');
    setTimeout(() => setFileUploadMessage(''), 2000);
  };

  return (
    <div className="page-editor-overlay">
      <div className="page-editor-container">
        <div className="page-editor-header">
          <h1>Edit Pages</h1>
          <div className="header-buttons">
            <button className="btn-close" onClick={onClose}>
              <FiX size={24} />
            </button>
          </div>
        </div>

        {message && (
          <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
            {message}
          </div>
        )}

        <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>
            <strong>Tip:</strong> This is an empty page. Fill in the title and content (supports HTML) to create a new page. The page will be automatically created when you save.
          </p>
        </div>

        <div className="page-editor-content">
          {/* Page List */}
          <div className="page-list">
            <h3>Pages</h3>
            {availablePages.length === 0 ? (
              <p style={{ color: '#999', fontSize: '0.9rem' }}>No pages available</p>
            ) : (
              availablePages.map((page) => (
                <button
                  key={page.id}
                  className={`page-btn ${selectedPage === page.id ? 'active' : ''}`}
                  onClick={() => setSelectedPage(page.id)}
                >
                  {page.title}
                </button>
              ))
            )}
          </div>

          {/* Page Editor */}
          <div className="page-editor-main">
            {loading ? (
              <div className="loading">Loading page...</div>
            ) : (
              <>
                <form onSubmit={handleSave}>
                  <div className="form-group">
                    <label htmlFor="page-title">Page Title</label>
                    <input
                      id="page-title"
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Enter page title"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="page-content">Page Content</label>
                    <textarea
                      id="page-content"
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Enter page content (supports HTML)"
                      rows="12"
                    />
                  </div>

                  <div className="editor-actions">
                    <button type="submit" className="btn-save" disabled={saving}>
                      <FiSave /> {saving ? 'Saving...' : 'Save Page'}
                    </button>
                  </div>
                </form>

                {/* File Upload Section */}
                <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #eee' }}>
                  <h3 style={{ marginBottom: '1rem' }}>Upload Files for This Page</h3>

                  {fileUploadMessage && (
                    <div style={{
                      padding: '0.75rem',
                      marginBottom: '1rem',
                      backgroundColor: fileUploadMessage.includes('✗') ? '#fee' : '#efe',
                      color: fileUploadMessage.includes('✗') ? '#c33' : '#3a3',
                      borderRadius: '4px',
                      fontSize: '0.9rem'
                    }}>
                      {fileUploadMessage}
                    </div>
                  )}

                  <div style={{
                    border: '2px dashed #ddd',
                    borderRadius: '4px',
                    padding: '2rem',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: '#fafafa',
                    marginBottom: '1rem'
                  }}>
                    <input
                      type="file"
                      id="file-input"
                      multiple
                      onChange={handleFileUpload}
                      disabled={uploading}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="file-input" style={{ cursor: 'pointer', display: 'block' }}>
                      <FiUpload size={32} style={{ marginBottom: '0.5rem', color: '#de4010' }} />
                      <p style={{ margin: '0.5rem 0', fontSize: '0.9rem' }}>
                        Click to upload or drag and drop
                      </p>
                      <p style={{ margin: '0.25rem 0', color: '#999', fontSize: '0.8rem' }}>
                        Max file size: 10MB
                      </p>
                    </label>
                  </div>

                  {/* File List */}
                  {pageFiles.length > 0 && (
                    <div style={{
                      backgroundColor: '#f9f9f9',
                      borderRadius: '4px',
                      padding: '1rem',
                      border: '1px solid #eee'
                    }}>
                      <h4 style={{ margin: '0 0 1rem 0', fontSize: '0.95rem' }}>
                        Uploaded Files ({pageFiles.length})
                      </h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        {pageFiles.map((file) => (
                          <div
                            key={file.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem',
                              padding: '0.75rem',
                              backgroundColor: 'white',
                              borderRadius: '4px',
                              border: '1px solid #eee'
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <p style={{ margin: '0 0 0.25rem 0', fontWeight: '500', fontSize: '0.9rem' }}>
                                {file.file_name}
                              </p>
                              <p style={{ margin: 0, color: '#666', fontSize: '0.75rem' }}>
                                Size: {(file.file_size / 1024).toFixed(2)} KB
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => copyToClipboard(file.file_url)}
                              title="Copy file URL"
                              style={{
                                padding: '0.5rem 0.75rem',
                                background: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                            >
                              <FiCopy size={14} /> Copy
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteFile(file.id)}
                              title="Delete file"
                              style={{
                                padding: '0.5rem 0.75rem',
                                background: '#dc3545',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem'
                              }}
                            >
                              <FiTrash2 size={14} /> Delete
                            </button>
                          </div>
                        ))}
                      </div>
                      <p style={{
                        margin: '1rem 0 0 0',
                        padding: '0.75rem',
                        backgroundColor: '#f0f0f0',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        color: '#666'
                      }}>
                        <strong>Tip:</strong> Click "Copy" to copy the file URL, then paste it in your page content as an image tag: &lt;img src="[URL]" /&gt;
                      </p>
                    </div>
                  )}
                </div>

                {/* Preview Section */}
                {content && (
                  <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid #eee' }}>
                    <h3 style={{ marginBottom: '1rem' }}>Preview</h3>
                    <div style={{
                      padding: '1.5rem',
                      backgroundColor: '#f9f9f9',
                      borderRadius: '4px',
                      border: '1px solid #eee',
                      maxHeight: '400px',
                      overflowY: 'auto'
                    }}>
                      <div dangerouslySetInnerHTML={{ __html: content }} />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
